import SlidingPanel from "@components/sliders/SlidingPanel";
import { userManagementApis } from "@services/apis/UserManagement";
import { ActiveSession } from "@services/models/UserManagement/active-sessions";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";

interface ActiveSessionSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (success: boolean) => void;
  userId: number;
}

const ActiveSessionSlider: React.FC<ActiveSessionSliderProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userId,
}) => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);

  const fetchActiveSessions = async () => {
    setLoading(true);
    try {
      const response = await userManagementApis.getActiveSessions(userId);
      setActiveSessions(response.data.activeSessions);
      setLoading(false);
    } catch (error: any) {
      setError(error.message || "Failed to fetch active sessions.");
      setLoading(false);
    }
  };

  const terminateSession = async () => {
    
    try {
      await userManagementApis.revokeSession({
        revokeType: "all",
        userId,
      });
      fetchActiveSessions();
    } catch (error: any) {
      setError(error.message || "Failed to terminate session.");
    }
  };

  useEffect(() => {
    if (isOpen) fetchActiveSessions();
  }, [isOpen, userId]);

  if (error) {
    return (
      <SlidingPanel isOpen={isOpen} onClose={onClose}>
        <div className="p-4 text-red-500">{error}</div>
      </SlidingPanel>
    );
  }

  return (
    <div className="flex flex-col bg-bg-base">
      {/* Loading */}
      {loading &&
        [...Array(3)].map((_, i) => (
          <div
            key={i}
            className="flex flex-row border-b border-border-subtle py-[8px] px-[16px] justify-between">
            <div className="flex flex-col gap-[8px]">
              <Skeleton width="120px" height="12px" />

              <Skeleton width="60px" height="15px" />
            </div>

            <Skeleton width="70px" height="20px" />
          </div>
        ))}

      {/* Error */}
      {!loading && error && (
        <div className="p-4 text-red-500 text-[12px]">{error}</div>
      )}

      {/* No sessions */}
      {!loading && !error && activeSessions.length === 0 && (
        <div className="p-4 text-text-subtle text-[12px] text-center">
          No active sessions
        </div>
      )}
      {activeSessions.map((session, index) => {
        return (
          <div className="flex flex-row border-b border-border-subtle py-[8px] px-[16px] justify-between items-start">
            <div className="flex flex-col gap-[8px] items-start">
              <span className="text-text-base text-[12px] font-medium">
                {session.value.deviceName}
              </span>
              <span className="inline-flex items-center text-text-base text-[10px] font-normal border border-border-subtle rounded-full px-[8px] py-[4px] whitespace-nowrap">
                {session.value.ip}
              </span>
            </div>
            <div
              className="inline-flex py-[4px] items-center justify-center px-[6px] rounded-md text-text-status-red border border-text-status-red text-[10px] font-medium cursor-pointer"
              onClick={() => terminateSession()}>
              TERMINATE
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ActiveSessionSlider;
